import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devLog, devError } from '../../../utils/devLogger';
import CmsPopup from '../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../components/CmsPopupBtn';
import { getAuctionDetail } from '../../../lib/api/admin/adminApi';
import AuctionLotsList from './AuctionLotsList';
import CurrentLotInfo from './CurrentLotInfo';
import BidHistoryTable from './BidHistoryTable';
import { PopupContainer, RightSection } from './AuctionDetailStyles';
import * as XLSX from 'xlsx';

// LOT 데이터 타입 (사전입찰용)
export interface LotData {
  lotId: string;
  lotName: string;
  thumbnail: string;
  images?: string[];
  status: 'WAITING'; // 사전입찰은 모두 진행예정
  startPriceKrw: number;
  startPriceUsd: number;
  currentPriceKrw: number;
  currentPriceUsd: number;
  bidHistory: PreBidHistoryItem[];
}

// 사전입찰 내역 아이템 타입 (BidHistoryItem과 호환되도록 수정)
export interface PreBidHistoryItem {
  key: string;
  no: number;
  bidTime: string;
  bidder: string;
  siteNumber: string; // 추가
  country: string;
  bidAmountKrw: string;
  bidAmountUsd: string;
  contact: string;
  email: string;
  bidRestricted: string; // 추가
  status: string;
  isPreBidder: true; // 모두 사전입찰자
}

// 경매 상세 데이터 타입 (API 응답에 맞춤)
interface AuctionDetailData {
  auctionIndex: number;
  auctionName: string;
  auctioneerName: string;
  auctionStartAt: string;
  auctionEndAt: string;
  status: string;
  lots: any[];
  preBids?: any[]; // 사전입찰 데이터 추가
}

interface PreBidDetailPopupProps {
  isOpen: boolean;
  auctionIndex: number;
  onClose: () => void;
}

const PreBidDetailPopup: React.FC<PreBidDetailPopupProps> = ({
  isOpen,
  auctionIndex,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [auctionData, setAuctionData] = useState<AuctionDetailData | null>(null);
  const [selectedLotId, setSelectedLotId] = useState<string>('');

  // 경매 상세 데이터 조회
  const fetchAuctionData = useCallback(async () => {
    if (!auctionIndex) return;

    setLoading(true);
    try {
      devLog('📊 [PreBidDetailPopup] 경매 상세 데이터 조회 시작:', auctionIndex);

      // 경매 상세 정보 조회
      const auctionResponse = await getAuctionDetail(auctionIndex);

      devLog('📊 [PreBidDetailPopup] API 응답:', {
        auctionResponse: auctionResponse?.data,
        dataStructure: auctionResponse?.data?.data
      });

      const auction = auctionResponse?.data?.data;

      if (auction) {
        setAuctionData({
          auctionIndex: auction.index,
          auctionName: auction.name,
          auctioneerName: auction.auctioneer?.name || '경매사 없음',
          auctionStartAt: auction.auctionStartAt,
          auctionEndAt: auction.auctionEndAt,
          status: auction.status || 'UNKNOWN',
          lots: auction.lots || [],
          preBids: auction.preBids || [] // 사전입찰 데이터 추가
        });
      }
    } catch (error) {
      devError('경매 상세 데이터 조회 에러:', error);
    } finally {
      setLoading(false);
    }
  }, [auctionIndex]);

  // 팝업이 열릴 때 데이터 조회
  useEffect(() => {
    if (isOpen && auctionIndex) {
      fetchAuctionData();
    }
  }, [isOpen, auctionIndex, fetchAuctionData]);

  // 소켓 데이터를 UI에 맞는 형태로 변환 - 사전입찰용
  const auctionLots: LotData[] = useMemo(() => {
    if (!auctionData?.lots) {
      devLog('📊 [PreBidDetailPopup] auctionLots 생성 실패 - 데이터 없음:', auctionData);
      return [];
    }
    
    const lots = auctionData.lots.map(lot => ({
      lotId: lot.lotCode || `LOT${lot.lotNo?.toString().padStart(3, '0')}`,
      lotName: lot.lotName || '작품명 없음',
      thumbnail: lot.images?.[0] || 'thumbnail.jpg',
      images: lot.images || [],
      status: 'WAITING' as const, // 사전입찰은 모두 진행예정
      startPriceKrw: lot.startPrice || 0,
      startPriceUsd: lot.startPriceUsd || 0,
      currentPriceKrw: lot.startPrice || 0, // 사전입찰에서는 시작가와 동일
      currentPriceUsd: lot.startPriceUsd || 0,
      bidHistory: [] // 개별 lot의 사전입찰 내역은 선택 시 로드
    }));

    devLog('📊 [PreBidDetailPopup] auctionLots 생성 완료:', {
      rawLots: auctionData.lots,
      transformedLots: lots
    });

    return lots;
  }, [auctionData]);

  // 현재 선택된 LOT 데이터 가져오기 + 사전입찰 데이터 로드
  const currentLot = useMemo(() => {
    const lot = auctionLots.find(lot => lot.lotId === selectedLotId);
    if (!lot) return null;

    // 현재 선택된 lot의 실제 데이터 찾기
    const selectedLotData = auctionData?.lots?.find(auctionLot => 
      auctionLot && (auctionLot.lotCode === selectedLotId || 
                     `LOT${auctionLot.lotNo?.toString().padStart(3, '0')}` === selectedLotId)
    );

    // API 응답에서 preBids 데이터를 가져와서 변환
    const preBidHistory: PreBidHistoryItem[] = [];
    
    if (auctionData && selectedLotData) {
      // 경매 전체의 preBids에서 현재 LOT에 해당하는 것만 필터링
      const auctionWithPreBids = auctionData as AuctionDetailData & { preBids?: any[] };
      const lotPreBids = auctionWithPreBids.preBids?.filter((preBid: any) => 
        preBid.lotIndex === selectedLotData.index
      ) || [];

      devLog('📊 [PreBidDetailPopup] 사전입찰 데이터 변환:', {
        selectedLotId,
        selectedLotData,
        auctionDataLots: auctionData?.lots,
        totalPreBids: auctionWithPreBids.preBids?.length || 0,
        filteredPreBids: lotPreBids.length,
        lotPreBids
      });

      // PreBidHistoryItem 형태로 변환
      lotPreBids.forEach((preBid: any, index: number) => {
        preBidHistory.push({
          key: preBid.index.toString(),
          no: index + 1,
          bidTime: new Date(preBid.createAt).toLocaleString('ko-KR'),
          bidder: preBid.user?.name || '사전입찰자',
          siteNumber: `F${preBid.index.toString().padStart(4, '0')}`, // 사전입찰자는 F로 시작
          country: '대한민국', // 기본값
          bidAmountKrw: `₩${preBid.price.toLocaleString()}`,
          bidAmountUsd: `$${(preBid.price / (selectedLotData.exchangeRate || 1200)).toFixed(2)}`,
          contact: '-', // 사전입찰에서는 연락처 정보 없음
          email: '-', // 사전입찰에서는 이메일 정보 없음
          bidRestricted: 'N', // 사전입찰은 제한 없음
          status: preBid.isWon ? '낙찰' : '진행중',
          isPreBidder: true
        });
      });
    }

    return {
      ...lot,
      bidHistory: preBidHistory
    };
  }, [auctionLots, selectedLotId, auctionData]);

  // 첫 번째 LOT을 기본 선택
  useEffect(() => {
    if (auctionLots.length > 0 && !selectedLotId) {
      setSelectedLotId(auctionLots[0].lotId);
    }
  }, [auctionLots, selectedLotId]);

  // LOT 선택 핸들러
  const handleLotSelect = useCallback((lotId: string) => {
    devLog('👤 [PreBidDetailPopup] 사용자가 직접 LOT 선택:', lotId);
    setSelectedLotId(lotId);
  }, []);

  // 엑셀 다운로드 핸들러 (사전입찰용)
  const handleExcelDownload = useCallback(() => {
    if (!currentLot) {
      alert('다운로드할 경매 정보가 없습니다.');
      return;
    }

    try {
      devLog('📊 [PreBidDetailPopup] 엑셀 다운로드 시작:', {
        lotId: currentLot.lotId,
        bidHistoryCount: currentLot.bidHistory.length
      });

      // 엑셀 데이터 준비 (사전입찰 내역)
      const excelData = currentLot.bidHistory.map((bid) => ({
        'No': bid.no,
        '사전입찰일시': bid.bidTime,
        '입찰자': bid.bidder,
        '국가': bid.country,
        '입찰금액(KRW)': bid.bidAmountKrw.replace(/[₩,]/g, ''),
        '입찰금액(USD)': bid.bidAmountUsd.replace(/[$,]/g, ''),
        '연락처': bid.contact || '-',
        '이메일': bid.email || '-',
        '상태': bid.status
      }));

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 5 },   // No
        { wch: 20 },  // 사전입찰일시
        { wch: 15 },  // 입찰자
        { wch: 10 },  // 국가
        { wch: 18 },  // 입찰금액(KRW)
        { wch: 18 },  // 입찰금액(USD)
        { wch: 15 },  // 연락처
        { wch: 25 },  // 이메일
        { wch: 10 }   // 상태
      ];
      worksheet['!cols'] = columnWidths;

      // 워크시트를 워크북에 추가
      XLSX.utils.book_append_sheet(workbook, worksheet, '사전입찰내역');

      // 파일명 생성 (현재 날짜와 LOT ID 포함)
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const fileName = `사전입찰내역_${currentLot.lotId}_${dateStr}_${timeStr}.xlsx`;

      // 엑셀 파일 다운로드
      XLSX.writeFile(workbook, fileName);

      devLog('✅ [PreBidDetailPopup] 엑셀 다운로드 완료:', {
        fileName,
        recordCount: excelData.length
      });

    } catch (error) {
      devError('엑셀 다운로드 중 오류:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  }, [currentLot]);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>경매 상세 데이터를 불러오는 중...</p>
        </div>
      );
    }

    if (!auctionData) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>경매 상세 데이터를 불러올 수 없습니다.</p>
          <button 
            onClick={fetchAuctionData}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
        </div>
      );
    }

    // 현재 LOT이 없는 경우
    if (!currentLot) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>선택된 작품이 없습니다.</p>
        </div>
      );
    }

    return (
      <PopupContainer>
        {/* 왼쪽 영역 - 경매 Lots */}
        <AuctionLotsList
          lots={auctionLots}
          selectedLotId={selectedLotId}
          auctionStartAt={auctionData?.auctionStartAt}
          onLotSelect={handleLotSelect}
        />

        {/* 오른쪽 영역 - 선택된 LOT 상세 정보 */}
        <RightSection>
          {/* 현재 로트 정보 */}
          <CurrentLotInfo 
            currentLot={currentLot} 
            auctionEndAt={auctionData?.auctionEndAt}
          />

          {/* 사전입찰내역 */}
          <BidHistoryTable
            bidHistory={currentLot.bidHistory}
            bidRestrictions={{}} // 입찰제한 필요 없음
            bannedUsers={[]} // 차단된 사용자 필요 없음
            lotStatus={currentLot.status}
            auctionStartAt={auctionData?.auctionStartAt}
            auctionIndex={auctionIndex}
            preBids={[]} // 사전입찰 정보
            currentLotIndex={0}
            exchangeRate={1200}
            onExcelDownload={handleExcelDownload}
            onBidderRegister={() => {}} // 빈 함수
            onCountStart={() => {}} // 빈 함수  
            onBidRowClick={() => {}} // 빈 함수
            onBidRestrictionToggle={() => {}} // 빈 함수
            // 사전입찰용 옵션들
            isPreBidMode={true}
            hideSiteNumber={true}
            hideBidRestriction={true}
            hideActionButtons={true}
          />
        </RightSection>
      </PopupContainer>
    );
  };

  return (
    <CmsPopup
      title="경매 상세 조회"
      isOpen={isOpen}
      onClose={onClose}
      width="1200px"
      showRequiredMark={false}
      leftButtons={[]}
      rightButtons={[
        {
          label: '닫기',
          onClick: onClose,
          type: CmsPopupBtnType.SECONDARY,
        },
      ]}
    >
      {renderContent()}
    </CmsPopup>
  );
};

export default PreBidDetailPopup;